import { authFetch } from '../../api/csrf';
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  handleTextInput,
  handleTimeInput,
  validateText,
  validateTime,
  getCharacterCount,
  FIELD_LIMITS
} from '../../utils/inputValidation';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Edit,
  Upload,
  ChevronLeft,
  ChevronRight,
  Home,
  Zap,
  Award,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Send,
  Download,
  Eye,
  ArrowLeft,
  ArrowRight,
  Timer,
  User,
  FileText,
  X,
  Trash2
} from 'lucide-react';
import Button from '../../components/common/Button/Button';
import ExportModal from '../../components/common/ExportModal.jsx';
import essService from '../../api/services/essService';
import { regularizationReasonService } from "../../api/crm/config/configService";
import { toast } from 'react-hot-toast';
import HolidayCalendar from '../hrms/attendance-leaves/HolidayCalendar';
import { formatDate } from '../../utils/stringUtils';
import {
  exportToCSV,
  generateExportFilename,
  downloadFromBackend
} from '../../utils/exportUtils';

const formatTimeAMPM = (timeStr) => {
  if (!timeStr || timeStr === 'N/A') return 'N/A';
  // Handle if timeStr already has AM/PM
  if (timeStr.includes('AM') || timeStr.includes('PM')) return timeStr;

  try {
    const [hours, minutes] = timeStr.split(':');
    if (!hours || !minutes) return timeStr;

    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);

    if (isNaN(h) || isNaN(m)) return timeStr;

    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  } catch (e) {
    return timeStr;
  }
};

const MyAttendance = () => {
  const [activeTab, setActiveTab] = useState('attendance');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayView, setShowDayView] = useState(false);
  const [showRegularizationModal, setShowRegularizationModal] = useState(false);
  const [showCompOffModal, setShowCompOffModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState(null);
  const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLogs: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // --- Selection & Export States ---
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Clear filters
  const clearFilters = () => {
    setFilterStatus('all');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPageSize(limit);
    setCurrentPage(1);
  };

  // Attendance data for today (for clock in/out)
  const [attendanceData, setAttendanceData] = useState({
    today: {
      date: new Date().toISOString().split('T')[0],
      status: null,
      hours: 0,
      checkIn: null,
      checkOut: null,
      anomalies: [],
      punches: [],
      sessions: [] // Store all sessions for today
    }
  });

  const [regularizationRequests, setRegularizationRequests] = useState([]);
  const [regularizationPagination, setRegularizationPagination] = useState({
    totalItems: 0,
    totalPages: 1
  });
  const [regularizationPage, setRegularizationPage] = useState(1);
  const [regularizationPageSize, setRegularizationPageSize] = useState(10);

  const [compOffData, setCompOffData] = useState({
    available: 0,
    used: 0,
    pending: 0,
    history: []
  });
  const [rosterPagination, setRosterPagination] = useState({
    totalItems: 0,
    totalPages: 1
  });
  const [compOffPage, setCompOffPage] = useState(1);
  const [compOffPageSize, setCompOffPageSize] = useState(10);

  const [overtimeData, setOvertimeData] = useState({
    total: 0,
    used: 0,
    pending: 0,
    history: []
  });
  const [overtimePage, setOvertimePage] = useState(1);
  const [overtimePageSize, setOvertimePageSize] = useState(10);

  const [holidays, setHolidays] = useState([]);

  const [shiftData, setShiftData] = useState({
    current: {
      name: null,
      startTime: null,
      endTime: null,
      breakDuration: 0,
      workingDays: []
    },
    roster: []
  });
  const [rosterPage, setRosterPage] = useState(1);
  const [rosterPageSize, setRosterPageSize] = useState(10);

  const [regularizationForm, setRegularizationForm] = useState({
    date: '',
    checkIn: '',
    checkOut: '',
    reason: '',
    reasonId: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Fetch regularization reasons for the form
  const { data: reasonsData } = useQuery({
    queryKey: ["regularizationReasons", "dropdown"],
    queryFn: () => regularizationReasonService.getDropdown()
  });
  const dynamicReasons = reasonsData?.data || [];

  // Use React Query to cache employee profile (shared across all functions)
  const {
    data: employeeProfile,
    isLoading: profileLoading
  } = useQuery({
    queryKey: ['employeeProfile'],
    queryFn: async () => {
      try {
        const result = await essService.getProfile();
        if (result.success && result.data) {
          return result.data;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    staleTime: 0,
  });

  // Set employee ID from cached profile
  useEffect(() => {
    if (employeeProfile) {
      // Use the string employeeId (e.g., "EMP001") for searching/filtering
      // Fallback to _id if employeeId string is not present
      setEmployeeId(employeeProfile.employeeId || employeeProfile._id || employeeProfile.id);
    }
  }, [employeeProfile]);

  // Initial loading state control
  useEffect(() => {
    if (employeeProfile && !profileLoading) {
      setInitialLoading(false);
    }
  }, [employeeProfile, profileLoading]);

  // Load Today's Attendance summary when tab is active
  useEffect(() => {
    if (employeeId && activeTab === 'attendance') {
      loadTodayAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab]);

  // Load attendance logs when attendance tab is active and filters/page change
  useEffect(() => {
    if (employeeId && activeTab === 'attendance') {
      loadAttendanceLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab, currentPage, itemsPerPage, filterStatus, fromDate, toDate]);

  // Load regularization requests when regularization tab is active
  useEffect(() => {
    if (employeeId && activeTab === 'regularization') {
      loadRegularizationRequests(regularizationPage, regularizationPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab, regularizationPage, regularizationPageSize]);

  // Load shift and roster when shift tab is active
  useEffect(() => {
    if (employeeId && activeTab === 'shift') {
      loadShiftAndRoster(rosterPage, rosterPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab, rosterPage, rosterPageSize]);

  // Load holidays when holidays tab is active
  useEffect(() => {
    if (employeeId && activeTab === 'holidays') {
      loadHolidays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, activeTab]);

  // --- Selection & Export Logic ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = attendanceLogs.map(log => log.id || log.logId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    if (!id) return;
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const mapLogsForExport = (logs) => {
    const employeeName = employeeProfile ?
      `${employeeProfile.firstName || ''} ${employeeProfile.lastName || ''}`.trim() || employeeProfile.fullName
      : 'N/A';

    return logs.map(log => {
      let workingHours = log.workingHours;
      if (!workingHours || workingHours === 'N/A' || workingHours === 0) {
        workingHours = calculateWorkingHours(log.checkIn, log.checkOut);
      }

      return {
        'Employee ID': log.employeeId || employeeId || 'N/A',
        'Employee Name': log.employeeName || employeeName || 'N/A',
        'Date': new Date(log.date).toLocaleDateString('en-GB'),
        'Check In': log.checkIn || 'N/A',
        'Check Out': log.checkOut || 'N/A',
        'Working Hours': workingHours,
        'Status': log.status || 'N/A'
      };
    });
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      const logsToExport = attendanceLogs.filter(log =>
        selectedRows.has(log.id || log.logId)
      );
      const exportData = mapLogsForExport(logsToExport);

      if (exportData.length === 0) {
        toast.error('No valid rows selected to export');
        return;
      }

      exportToCSV(exportData, 'attendance_selected', {
        title: 'Attendance',
        type: 'Selected Records'
      });
      toast.success('Selected records exported successfully');
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    if (attendanceLogs.length === 0) {
      toast.error('No data to export');
      return;
    }
    const exportData = mapLogsForExport(attendanceLogs);
    exportToCSV(exportData, 'attendance_page', {
      title: 'Attendance',
      type: 'Page'
    });
    setShowExportModal(false);
    toast.success('Current page exported successfully');
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (employeeId) queryParams.append("search", employeeId);
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (filterStatus !== "all") queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/export?${queryParams.toString()}`;
      const filename = generateExportFilename('attendance_range');
      await downloadFromBackend(url, filename, token);

      setShowExportModal(false);
      toast.success('Export started successfully');
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (employeeId) queryParams.append("search", employeeId);
      queryParams.append("type", "All Data");
      if (filterStatus !== "all") queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/export?${queryParams.toString()}`;
      const filename = generateExportFilename('all_attendance');
      await downloadFromBackend(url, filename, token);

      setShowExportModal(false);
      toast.success('All data export started');
    } catch (error) {
      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  // Load attendance logs from HRMS API (same as HRMS attendance logs)
  const loadAttendanceLogs = async () => {
    if (!employeeId) return;

    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", itemsPerPage.toString());
      // The backend expects 'search' for filtering by ID/Name in biometric-attendance
      queryParams.append("search", employeeId);
      if (filterStatus !== "all") queryParams.append("status", filterStatus);
      if (fromDate) queryParams.append("startDate", fromDate);
      if (toDate) queryParams.append("endDate", toDate);

      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance?${queryParams.toString()}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: "GET",
        headers: {
          "Content-Type": "application/json",
                  },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.responseCode === 200) {
        const logs = data.data.logs || [];
        const rawPagination = data.data.pagination || {
          currentPage,
          totalPages: 1,
          totalLogs: logs.length,
          hasNextPage: false,
          hasPrevPage: false,
          limit: itemsPerPage
        };

        const normalizedPagination = {
          currentPage: rawPagination.currentPage || currentPage,
          totalPages: Math.max(rawPagination.totalPages || 1, 1),
          totalLogs: rawPagination.totalLogs ?? logs.length,
          hasNextPage: rawPagination.hasNextPage ?? false,
          hasPrevPage: rawPagination.hasPrevPage ?? false,
          limit: rawPagination.limit ?? itemsPerPage
        };

        setAttendanceLogs(logs);
        setPagination(normalizedPagination);

        if (normalizedPagination.totalPages > 0 && currentPage > normalizedPagination.totalPages) {
          setCurrentPage(normalizedPagination.totalPages);
        }
      } else {
        throw new Error(data.responseMessage || 'Failed to load attendance logs');
      }
    } catch (error) {
      toast.error('Failed to load attendance logs');
      setAttendanceLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Load today's attendance using biometric-attendance API
  const loadTodayAttendance = async () => {
    if (!employeeId) return;

    try {
      // Fetch today's logs for the employee to calculate stats
      const today = new Date().toISOString().split('T')[0];
      const queryParams = new URLSearchParams();
      // Use search param to filter by employee ID
      queryParams.append("search", employeeId);
      queryParams.append("startDate", today);
      queryParams.append("endDate", today);

      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance?${queryParams.toString()}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: "GET",
        headers: {
          "Content-Type": "application/json",
                  },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200 && data.data && data.data.logs && data.data.logs.length > 0) {
          const todayLog = data.data.logs[0];

          // Parse time strings (e.g., "09:30:00")
          const parseTimeMinutes = (timeStr) => {
            if (!timeStr || timeStr === 'N/A') return 0;
            const parts = timeStr.split(':').map(Number);
            if (parts.length < 2) return 0;
            return parts[0] * 60 + parts[1];
          };

          const formatDuration = (minutes) => {
            if (minutes <= 0) return 0;
            return parseFloat((minutes / 60).toFixed(2));
          };

          const checkInMins = parseTimeMinutes(todayLog.checkIn);
          const checkOutMins = parseTimeMinutes(todayLog.checkOut);

          let workingHours = 0;
          if (checkInMins && checkOutMins) {
            let diff = checkOutMins - checkInMins;
            if (diff < 0) diff += 24 * 60;
            workingHours = formatDuration(diff);
          }

          setAttendanceData(prev => ({
            ...prev,
            today: {
              date: today,
              status: todayLog.status || 'Present',
              hours: workingHours,
              checkIn: todayLog.checkIn !== 'N/A' ? todayLog.checkIn : null,
              checkOut: todayLog.checkOut !== 'N/A' ? todayLog.checkOut : null,
              anomalies: [],
              punches: todayLog.checkInPunches || [], // Using punches from biometric log
              sessions: [] // Can be populated if needed
            }
          }));
        } else {
          // No logs found for today
          setAttendanceData(prev => ({
            ...prev,
            today: {
              date: new Date().toISOString().split('T')[0],
              status: 'Absent',
              hours: 0,
              checkIn: null,
              checkOut: null,
              anomalies: [],
              punches: [],
              sessions: []
            }
          }));
        }
      }
    } catch (error) {
    }
  };

  // Load holidays
  const loadHolidays = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      const result = await essService.getHolidays(year, month);

      if (result.success && result.data) {
        // Handle both array response and wrapped response
        const holidaysArray = Array.isArray(result.data) ? result.data : (result.data.holidays || []);
        const transformedHolidays = holidaysArray.map(holiday => ({
          date: holiday.date,
          name: holiday.name,
          type: holiday.type || 'national'
        }));
        setHolidays(transformedHolidays);
      } else {
        // If API fails, set empty array instead of fallback data
        setHolidays([]);
      }
    } catch (error) {
      // Set empty array on error - no fallback data
      setHolidays([]);
    }
  };

  // Load regularization requests
  const loadRegularizationRequests = async (page = regularizationPage, limit = regularizationPageSize) => {
    try {
      // Use cached employee profile from React Query
      if (!employeeProfile) {
        setRegularizationRequests([]);
        return;
      }

      const employeeId = employeeProfile._id || employeeProfile.id;

      if (!employeeId) {
        setRegularizationRequests([]);
        return;
      }

      // Fetch regularization requests for this employee
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', Math.min(limit, 10).toString());

      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization/employee/${employeeId}?${params.toString()}`, {
        credentials: 'include',
          method: "GET",
          headers: {
            "Content-Type": "application/json",
                      }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success || data.responseCode === 200) {
          const payload = data.data || data;
          const requestsSource = Array.isArray(payload) ? payload : (payload.requests || payload.data?.requests || []);
          const requests = Array.isArray(requestsSource) ? requestsSource : [];
          const pagination = payload.pagination || payload.data?.pagination || {};
          // Helper function to extract time from Date or time string
          // This matches the backend's toTimeString().split(' ')[0] format
          const extractTime = (timeValue) => {
            if (!timeValue) return '';
            // If it's already a time string (HH:MM or HH:MM:SS), return first 5 chars
            if (typeof timeValue === 'string' && timeValue.match(/^\d{2}:\d{2}/)) {
              return timeValue.substring(0, 5);
            }
            // If it's a Date object or ISO string, extract time using toTimeString() method
            // This matches how the backend formats times in approve/reject responses
            try {
              const date = new Date(timeValue);
              if (!isNaN(date.getTime())) {
                // Use toTimeString() to get HH:MM:SS format, then take first 5 chars for HH:MM
                // This gives us the time in the browser's local timezone, which should match
                // the server's local timezone if they're the same, or we need to handle timezone
                const timeString = date.toTimeString().split(' ')[0]; // Gets "HH:MM:SS"
                return timeString.substring(0, 5); // Returns "HH:MM"
              }
            } catch (e) {
            }
            return '';
          };

          // Transform requests to match frontend structure
          const transformedRequests = requests.map(request => ({
            id: request._id || request.requestId,
            requestId: request.requestId,
            date: request.date ? new Date(request.date).toISOString().split('T')[0] : '',
            checkIn: extractTime(request.requestedCheckIn),
            checkOut: extractTime(request.requestedCheckOut),
            reason: request.reason || '',
            description: request.description || '',
            status: request.status || 'pending',
            submittedAt: request.submittedDate || request.createdAt,
            approvedBy: request.approvedBy ? (request.approvedBy.firstName && request.approvedBy.lastName
              ? `${request.approvedBy.firstName} ${request.approvedBy.lastName}`.trim()
              : request.approvedByName || null) : (request.approvedByName || null),
            approvedDate: request.approvedDate,
            rejectionReason: request.rejectionReason,
            comments: request.comments
          }));
          setRegularizationRequests(transformedRequests);
          const resolvedTotalItems = pagination.totalItems ?? requests.length;
          const resolvedTotalPages = pagination.totalPages ?? Math.max(1, Math.ceil((resolvedTotalItems || 0) / limit));
          if (resolvedTotalPages > 0 && page > resolvedTotalPages) {
            setRegularizationPage(resolvedTotalPages);
          }
          setRegularizationPagination({
            totalItems: resolvedTotalItems,
            totalPages: resolvedTotalPages
          });
        } else {
          setRegularizationRequests([]);
          setRegularizationPagination({
            totalItems: 0,
            totalPages: 1
          });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setRegularizationRequests([]);
        setRegularizationPagination({
          totalItems: 0,
          totalPages: 1
        });
      }
    } catch (error) {
      setRegularizationRequests([]);
      setRegularizationPagination({
        totalItems: 0,
        totalPages: 1
      });
    }
  };

  // Status helper functions (matching HRMS attendance logs format)
  const getStatusColor = (status) => {
    if (!status) return 'text-gray-600 bg-gray-100';
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'present': return 'bg-green-100 text-green-800';
      case 'late': return 'bg-yellow-100 text-yellow-800';
      case 'half_day': return 'bg-orange-100 text-orange-800';
      case 'absent': return 'bg-red-100 text-red-800';
      case 'wfh': return 'bg-blue-100 text-blue-800';
      case 'od': return 'bg-purple-100 text-purple-800';
      case 'holiday': return 'bg-orange-100 text-orange-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Load shift and roster data for current employee
  const loadShiftAndRoster = async (page = rosterPage, limit = rosterPageSize) => {
    try {
      // Try to get employee ID from user data in localStorage first
      let employeeId = null;
      try {
        const userData = localStorage.getItem('user_data');
        if (userData) {
          const user = JSON.parse(userData);
          // Check if user has employeeId directly
          if (user.employeeId) {
            employeeId = user.employeeId;
          } else if (user.employee && user.employee._id) {
            employeeId = user.employee._id;
          }
        }
      } catch (e) {
      }

      // If not found in localStorage, try to get from backend using a simple endpoint
      // that doesn't require self_service permission
      if (!employeeId) {
        try {
          // Try to get employee by user ID from a simple endpoint
          const userResponse = await authFetch(
            `${process.env.REACT_APP_API_BASE_URL}/roster/current-user`, {
        credentials: 'include',
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                              }
            }
          );

          if (userResponse.ok) {
            const userData = await userResponse.json();
            employeeId = userData.data?.employeeId || userData.employeeId;
          }
        } catch (e) {
        }
      }

      // Use cached employee profile from React Query
      // Always use MongoDB _id for roster queries (not employeeId string)
      if (!employeeId && employeeProfile) {
        employeeId = employeeProfile._id || employeeProfile.id;
      } else if (employeeId && !employeeId.match(/^[0-9a-fA-F]{24}$/) && employeeProfile) {
        // If employeeId is a string like "EMP001", get the MongoDB _id from profile
        employeeId = employeeProfile._id || employeeProfile.id;
      }

      if (!employeeId) {
        setShiftData({
          current: {
            name: null,
            startTime: null,
            endTime: null,
            breakDuration: 0,
            workingDays: []
          },
          roster: []
        });
        setRosterPagination({
          totalItems: 0,
          totalPages: 1
        });
        return;
      }

      // Store base shift from employee profile (this should never be modified by rosters)
      let baseShift = null;
      if (employeeProfile && employeeProfile.employmentInfo && employeeProfile.employmentInfo.shift) {
        baseShift = employeeProfile.employmentInfo.shift;
      }

      // Fetch roster assignments for this employee
      const params = new URLSearchParams();
      params.append('employeeId', employeeId);
      params.append('page', page.toString());
      params.append('limit', Math.min(limit, 10).toString());

      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/roster?${params.toString()}`, {
        credentials: 'include',
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { })
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const rostersPayload = data.data?.rosters ?? data.data;
        const rosters = Array.isArray(rostersPayload) ? rostersPayload : [];
        const pagination = data.data?.pagination || data.pagination || {};

        // Check for active roster that overrides base shift for today
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let activeRoster = null;
        for (const roster of rosters) {
          const startDate = new Date(roster.startDate);
          const endDate = new Date(roster.endDate);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);

          // Check if today falls within this roster's date range
          if (today >= startDate && today <= endDate) {
            activeRoster = roster;
            break; // Use the first active roster found
          }
        }

        // Determine current shift: roster override takes priority, then base shift
        let currentShift = null;
        if (activeRoster && activeRoster.shiftId) {
          // Use roster shift (override)
          let shiftDetails = activeRoster.shiftId;

          // If shiftId is an object but doesn't have full details, fetch it
          if (typeof shiftDetails === 'string' || (typeof shiftDetails === 'object' && !shiftDetails.name)) {
            const shiftIdToFetch = typeof shiftDetails === 'string' ? shiftDetails : (shiftDetails._id || shiftDetails.id);

            if (shiftIdToFetch) {
              try {
                const shiftResponse = await authFetch(
                  `${process.env.REACT_APP_API_BASE_URL}/shifts-test/${shiftIdToFetch}`, {
        credentials: 'include',
                    method: "GET",
                    headers: {
                      "Content-Type": "application/json"
                    }
                  }
                );
                if (shiftResponse.ok) {
                  const shiftData = await shiftResponse.json();
                  shiftDetails = shiftData.data?.shift || shiftData.data || shiftDetails;
                }
              } catch (error) {
              }
            }
          }

          if (shiftDetails && shiftDetails.name) {
            currentShift = {
              name: shiftDetails.name || 'N/A',
              startTime: shiftDetails.startTime || null,
              endTime: shiftDetails.endTime || null,
              breakDuration: shiftDetails.breakDuration || 0,
              workingDays: shiftDetails.workingDays || [],
              isRosterOverride: true, // Flag to indicate this is from roster
              rosterDateRange: `${formatDate(activeRoster.startDate, { includeTime: false })} - ${formatDate(activeRoster.endDate, { includeTime: false })}`
            };
          }
        }

        // If no active roster, use base shift
        if (!currentShift && baseShift) {
          currentShift = {
            name: baseShift.name || 'No shift assigned',
            startTime: baseShift.startTime || null,
            endTime: baseShift.endTime || null,
            breakDuration: baseShift.breakDuration || 0,
            workingDays: baseShift.workingDays || [],
            isRosterOverride: false
          };
        } else if (!currentShift) {
          // No shift at all
          currentShift = {
            name: null,
            startTime: null,
            endTime: null,
            breakDuration: 0,
            workingDays: []
          };
        }

        // Set current shift
        setShiftData(prev => ({
          ...prev,
          current: currentShift
        }));

        // Transform roster data for display
        const transformedRoster = rosters.map(roster => {
          let shiftName = 'N/A';
          let startTime = null;
          let endTime = null;
          let breakDuration = 0;

          if (roster.shiftId) {
            if (typeof roster.shiftId === 'object' && roster.shiftId.name) {
              shiftName = roster.shiftId.name;
              startTime = roster.shiftId.startTime;
              endTime = roster.shiftId.endTime;
              breakDuration = roster.shiftId.breakDuration || 0;
            } else if (typeof roster.shiftId === 'string') {
              shiftName = 'Loading...';
            }
          }

          return {
            date: roster.startDate,
            endDate: roster.endDate,
            shift: shiftName,
            startTime: startTime || 'N/A',
            endTime: endTime || 'N/A',
            breakDuration: breakDuration,
            status: roster.status || 'Upcoming'
          };
        });

        setShiftData(prev => ({
          ...prev,
          roster: transformedRoster
        }));
        const resolvedRosterTotal = pagination.totalItems ?? rosters.length;
        const resolvedRosterPages = pagination.totalPages ?? Math.max(1, Math.ceil((resolvedRosterTotal || 0) / limit));
        if (resolvedRosterPages > 0 && page > resolvedRosterPages) {
          setRosterPage(resolvedRosterPages);
        }
        setRosterPagination({
          totalItems: resolvedRosterTotal,
          totalPages: resolvedRosterPages
        });
      } else {
        setShiftData({
          current: {
            name: null,
            startTime: null,
            endTime: null,
            breakDuration: 0,
            workingDays: []
          },
          roster: []
        });
        setRosterPagination({
          totalItems: 0,
          totalPages: 1
        });
      }
    } catch (error) {
      setShiftData({
        current: {
          name: null,
          startTime: null,
          endTime: null,
          breakDuration: 0,
          workingDays: []
        },
        roster: []
      });
      setRosterPagination({
        totalItems: 0,
        totalPages: 1
      });
    }
  };

  const getStatusIcon = (status) => {
    if (!status) return <Clock className="h-4 w-4 text-gray-500" />;
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'present': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'late': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'half_day': return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'absent': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'wfh': return <Home className="h-4 w-4 text-blue-500" />;
      case 'od': return <MapPin className="h-4 w-4 text-purple-500" />;
      case 'holiday': return <CalendarIcon className="h-4 w-4 text-orange-500" />;
      case 'pending': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'approved': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getAnomalyIcon = (anomaly) => {
    switch (anomaly) {
      case 'late': return <AlertCircle className="w-3 h-3 text-yellow-600" />;
      case 'early': return <Clock className="w-3 h-3 text-blue-600" />;
      case 'overtime': return <Zap className="w-3 h-3 text-purple-600" />;
      case 'missed': return <XCircle className="w-3 h-3 text-red-600" />;
      default: return <AlertCircle className="w-3 h-3 text-gray-600" />;
    }
  };



  const calculateWorkingHours = (checkIn, checkOut) => {
    const parseMins = (timeStr) => {
      if (!timeStr || timeStr === 'N/A') return 0;
      const parts = timeStr.split(':').map(Number);
      if (parts.length < 2) return 0;
      return parts[0] * 60 + parts[1];
    };

    if (!checkIn || !checkOut || checkIn === 'N/A' || checkOut === 'N/A') return '0.00h';
    try {
      const startMinutes = parseMins(checkIn);
      const endMinutes = parseMins(checkOut);
      let diff = endMinutes - startMinutes;
      if (diff < 0) diff += 24 * 60;
      return (diff / 60).toFixed(2) + 'h';
    } catch (e) {
      return '0.00h';
    }
  };

  // Handle view details - fetch full log with sessions (same as HRMS)
  const handleViewDetails = async (log) => {
    setSelectedAttendanceRecord(log);
    setShowAttendanceDetails(true);

    // Fetch full log details with all sessions
    try {
      const logId = log.logId || log.id;
      // Extract actual log ID if it's a transformed ID
      const actualLogId = logId && typeof logId === 'string' && (logId.includes('_current')
        ? logId.split('_current')[0]
        : logId.includes('_session_')
          ? logId.split('_session_')[0]
          : logId) || logId;
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/${actualLogId}`, {
        credentials: 'include',
          method: "GET",
          headers: {
            "Content-Type": "application/json",
                      },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          setSelectedAttendanceRecord(data.data); // Update with full log data including sessions
        }
      }
    } catch (error) {
      // Keep the existing log data if fetch fails
    }
  };

  const validateField = (name, value, currentForm) => {
    switch (name) {
      case 'date':
        if (!value) return 'Date is required';
        return '';
      case 'reason':
        if (!value) return 'Reason is required';
        return '';
      case 'checkIn':
        if (!value) return 'Check In time is required';
        return '';
      case 'checkOut':
        if (!value) return 'Check Out time is required';
        if (currentForm.checkIn && value) {
          const checkInTime = new Date(`2000-01-01T${currentForm.checkIn}`);
          const checkOutTime = new Date(`2000-01-01T${value}`);
          if (checkOutTime <= checkInTime) return 'Check Out time must be after Check In time';
        }
        return '';
      case 'description':
        if (!value || !value.trim()) return 'Description is required';
        if (value.trim().length < 10) return 'Description must be at least 10 characters';
        if (value.trim().length > 500) return 'Description cannot exceed 500 characters';
        return '';
      default:
        return '';
    }
  };

  const handleFieldChange = (name, value) => {
    if (name === 'reason') {
      const reasonObj = dynamicReasons.find(r => (r._id || r.id) === value);
      if (reasonObj) {
        setRegularizationForm(prev => {
          const updatedForm = { ...prev, reason: reasonObj.name, reasonId: value };
          const error = validateField('reason', reasonObj.name, updatedForm);
          setFormErrors(prevErrors => ({ ...prevErrors, reason: error }));
          return updatedForm;
        });
      } else {
        setRegularizationForm(prev => {
          const updatedForm = { ...prev, reason: value, reasonId: "" };
          const error = validateField('reason', value, updatedForm);
          setFormErrors(prevErrors => ({ ...prevErrors, reason: error }));
          return updatedForm;
        });
      }
      return;
    }

    setRegularizationForm(prev => {
      const updatedForm = { ...prev, [name]: value };

      // Update errors immediately based on new value
      const error = validateField(name, value, updatedForm);
      setFormErrors(prevErrors => ({
        ...prevErrors,
        [name]: error,
        // Also re-validate checkout if checkin changes
        ...(name === 'checkIn' ? { checkOut: validateField('checkOut', updatedForm.checkOut, updatedForm) } : {})
      }));

      return updatedForm;
    });
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, regularizationForm[name], regularizationForm);
    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateRegularizationForm = () => {
    const newErrors = {};
    const allTouched = {};

    ['date', 'reason', 'checkIn', 'checkOut', 'description'].forEach(field => {
      const error = validateField(field, regularizationForm[field], regularizationForm);
      if (error) newErrors[field] = error;
      allTouched[field] = true;
    });

    setFormErrors(newErrors);
    setTouched(allTouched);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  const handleRegularizationSubmit = async () => {
    const validation = validateRegularizationForm();
    if (!validation.isValid) {
      // Get the first error message to show specific validation error
      const errorFields = Object.keys(validation.errors).filter(key => validation.errors[key]);
      if (errorFields.length > 0) {
        const firstError = validation.errors[errorFields[0]];
        toast.error(firstError || 'Please fix the errors in the form before submitting.');
      } else {
        toast.error('Please fill all required fields before submitting.');
      }

      // Scroll to first error field for better UX
      setTimeout(() => {
        const firstErrorField = document.querySelector('[aria-invalid="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorField.focus();
        }
      }, 100);
      return;
    }

    setLoading(true);
    try {
      const resolvedReasonId = regularizationForm.reasonId && !regularizationForm.reasonId.startsWith('hardcoded-')
        ? regularizationForm.reasonId
        : null;

      const requestData = {
        date: regularizationForm.date,
        requestedCheckIn: regularizationForm.checkIn,
        requestedCheckOut: regularizationForm.checkOut,
        reason: regularizationForm.reason,
        reasonId: resolvedReasonId,
        description: regularizationForm.description,
        requestType: "both",
        priority: "medium",
      };

      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization`, {
        credentials: 'include',
          method: "POST",
          headers: {
            "Content-Type": "application/json",
                      },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (data.success || data.responseCode === 201) {
        toast.success('Regularization request submitted successfully!');
        setShowRegularizationModal(false);
        setRegularizationForm({ date: '', checkIn: '', checkOut: '', reason: '', reasonId: '', description: '' });
        setFormErrors({});
        // Reload requests to show the new one
        setRegularizationPage(1);
        await loadRegularizationRequests(1, regularizationPageSize);
      } else {
        // Handle specific validation errors
        if (data.error && data.error.includes("workingHours")) {
          toast.error("Invalid time range: Check-out time must be after check-in time. Please correct the times.");
        } else if (data.error && data.error.includes("validation failed")) {
          toast.error("Validation failed: Please check all required fields and ensure times are valid.");
        } else if (data.error && data.error.includes("already exists")) {
          toast.error("A regularization request already exists for this date.");
        } else {
          toast.error(`Failed to submit request: ${data.message || data.error || "Unknown error"}`);
        }
      }
    } catch (error) {
      toast.error('Failed to submit regularization request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRegularization = async (requestId) => {
    const isConfirmed = await window.confirm('Are you sure you want to delete this regularization request?');
    if (!isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization/${requestId}`, {
        credentials: 'include',
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
                      },
        }
      );

      const data = await response.json();

      if (data.success || data.responseCode === 200) {
        toast.success('Regularization request deleted successfully!');
        // Reload requests to update the list
        await loadRegularizationRequests(regularizationPage, regularizationPageSize);
      } else {
        toast.error(`Failed to delete request: ${data.message || data.error || "Unknown error"}`);
      }
    } catch (error) {
      toast.error('Failed to delete regularization request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderAttendanceCalendar = () => {
    const attendanceTotalItems = Number(pagination?.totalLogs || attendanceLogs.length || 0);
    const attendanceTotalPages = Number(pagination?.totalPages || 1);
    const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
    const handleAttendancePageChange = (page) => {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const shouldShowPagination = !loading && attendanceTotalPages > 1 && attendanceTotalItems > effectivePaginationLimit;

    return (
      <div className="space-y-6">
        {/* Today's Status - Modern Card Design */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 p-8">
          {/* Header Section with Clock In Button */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Today's Attendance</h2>
              <p className="text-sm text-gray-500">{formatDate(new Date(), { includeTime: false })}</p>
            </div>
          </div>
          {/* Stats Cards - Modern Design */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-600">Check In</span>
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">{attendanceData.today.checkIn || <span className="text-gray-400">--:--:--</span>}</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-600">Check Out</span>
                <div className="p-2 bg-red-50 rounded-lg">
                  <Clock className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">{attendanceData.today.checkOut || <span className="text-gray-400">--:--:--</span>}</p>
            </div>
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-600">Total Hours</span>
                <div className="p-2 bg-green-50 rounded-lg">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-gray-900">{(attendanceData.today.hours || 0).toFixed(2)}<span className="text-lg text-gray-500">h</span></p>
            </div>
          </div>
        </div>

        {/* Attendance History Table - Using HRMS Logs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Logs</h2>
            <Button
              variant={selectedRows.size > 0 ? "solid" : "outline"}
              icon={<Download className="h-4 w-4" />}
              onClick={handleExportClick}
              className={selectedRows.size > 0 ? "bg-agrosaf-green hover:bg-green-700 text-white" : ""}
            >
              {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
            </Button>
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full h-12 px-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="present">Present</option>
                <option value="late">Late</option>
                <option value="half_day">Half Day</option>
                <option value="absent">Absent</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
                onClick={(e) => e.target.showPicker()}
                className="w-full h-12 px-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
                onClick={(e) => e.target.showPicker()}
                className="w-full h-12 px-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              />
            </div>
            {/* Clear Filters */}
            {(filterStatus !== 'all' || fromDate || toDate) && (
              <div className="relative flex-shrink-0">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  className="h-12"
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex flex-col justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
              <span className="text-gray-600 font-medium">Loading attendance history...</span>
            </div>
          ) : attendanceLogs.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">No attendance records found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={attendanceLogs.length > 0 && selectedRows.size === attendanceLogs.length}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Check In
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Check Out
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Working Hours
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {attendanceLogs.map((log, index) => {
                      const logDate = new Date(log.date);
                      const formattedDate = formatDate(logDate, { includeTime: false });
                      const dayName = logDate.toLocaleDateString('en-US', { weekday: 'short' }); // Get day name (Mon, Tue, etc.)

                      return (
                        <tr
                          key={log.id || log.logId || index}
                          className={`hover:bg-gray-50 transition-colors ${selectedRows.has(log.id || log.logId) ? 'bg-blue-50' : ''}`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={selectedRows.has(log.id || log.logId)}
                              onChange={() => handleSelectRow(log.id || log.logId)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formattedDate}
                            <div className="text-xs text-gray-500">{dayName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatTimeAMPM(log.checkIn)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatTimeAMPM(log.checkOut)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {calculateWorkingHours(log.checkIn, log.checkOut)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(log.status)}
                              <span
                                className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                                  log.status
                                )}`}
                              >
                                {log.status
                                  ? log.status.replace(/_/g, ' ')
                                    .split(' ')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ')
                                  : 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleViewDetails(log)}
                              className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                            >
                              <Eye className="h-4 w-4" />
                              <span>Sessions</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {shouldShowPagination && (
                <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                    {/* Results Count and Limit Selector */}
                    <div className="flex items-center space-x-4">
                      <div className="text-sm text-gray-700">
                        Showing {((pagination.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((pagination.currentPage || 1) * effectivePaginationLimit, attendanceTotalItems)} of {attendanceTotalItems} results
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700">Items per page:</label>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                          className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleAttendancePageChange((pagination.currentPage || 1) - 1)}
                        disabled={pagination.hasPrevPage === false || (pagination.currentPage || 1) <= 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>

                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {(() => {
                          const totalPages = pagination.totalPages || 1;
                          const currentPageNum = pagination.currentPage || 1;
                          const pages = [];

                          if (totalPages <= 10) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push(1);

                            if (currentPageNum <= 4) {
                              for (let i = 2; i <= 5; i++) {
                                pages.push(i);
                              }
                              pages.push('ellipsis');
                              pages.push(totalPages);
                            } else if (currentPageNum >= totalPages - 3) {
                              pages.push('ellipsis');
                              for (let i = totalPages - 4; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push('ellipsis');
                              for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                pages.push(i);
                              }
                              pages.push('ellipsis');
                              pages.push(totalPages);
                            }
                          }

                          return pages.map((page, index) => {
                            if (page === 'ellipsis') {
                              return (
                                <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                  ...
                                </span>
                              );
                            }

                            return (
                              <button
                                key={page}
                                onClick={() => handleAttendancePageChange(page)}
                                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                  ? 'bg-agrosaf-green text-white'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                  }`}
                              >
                                {page}
                              </button>
                            );
                          });
                        })()}
                      </div>

                      <button
                        onClick={() => handleAttendancePageChange((pagination.currentPage || 1) + 1)}
                        disabled={pagination.hasNextPage === false || (pagination.currentPage || 1) >= (pagination.totalPages || 1)}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderDayView = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">
            Attendance Details - {formatDate(selectedDate, { includeTime: false })}
          </h3>
          <button
            onClick={() => setShowDayView(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Punch Timeline</h4>
            <div className="space-y-2">
              {attendanceData.today.punches.map((punch, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900">{formatTimeAMPM(punch.time)}</span>
                      <span className="text-sm text-gray-500 capitalize">{punch.type}</span>
                    </div>
                    <div className="text-sm text-gray-600">{punch.location}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Check In</h4>
              <p className="text-2xl font-bold text-gray-900">{formatTimeAMPM(attendanceData.today.checkIn)}</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Check Out</h4>
              <p className="text-2xl font-bold text-gray-900">{formatTimeAMPM(attendanceData.today.checkOut) || '--:--'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Pagination helper function
  const renderPagination = (currentPage, totalPages, totalItems, itemsPerPage, onPageChange, startIndex, endIndex) => {
    if (totalPages <= 1) return null;

    const safeStartIndex = Math.min(startIndex, Math.max(totalItems - 1, 0));
    const displayStart = totalItems === 0 ? 0 : safeStartIndex + 1;
    const displayEnd = totalItems === 0 ? 0 : Math.min(endIndex, totalItems);

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              Showing {displayStart} to {displayEnd} of {totalItems}
            </div>
            <div className="text-sm text-gray-500">Items per page: {itemsPerPage}</div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg ${currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderRegularization = () => {
    const totalRegularizationRequests = regularizationPagination.totalItems || regularizationRequests.length;
    const totalRegularizationPages =
      regularizationPagination.totalPages ||
      Math.max(1, Math.ceil((regularizationPagination.totalItems || regularizationRequests.length) / regularizationPageSize));
    const regularizationStartIndex = (regularizationPage - 1) * regularizationPageSize;
    const regularizationEndIndex = regularizationStartIndex + regularizationRequests.length;

    const handleRegularizationPageChange = (page) => {
      setRegularizationPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Request Regularization</h3>
            <button
              onClick={() => {
                setRegularizationForm({ date: '', checkIn: '', checkOut: '', reason: '', reasonId: '', description: '' });
                setFormErrors({});
                setTouched({});
                setShowRegularizationModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              <span>New Request</span>
            </button>
          </div>

          <div className="space-y-4">
            {regularizationRequests.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No regularization requests found</p>
              </div>
            ) : (
              regularizationRequests.map((request) => (
                <div key={request.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-medium text-gray-900">
                      {formatDate(request.date, { includeTime: false })}
                    </h4>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                      {getStatusIcon(request.status)}
                      <span className="ml-1 capitalize">{request.status.replace(/_/g, ' ')}</span>
                    </span>
                    {request.status === 'pending' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRegularization(request.id || request._id);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-2"
                        title="Delete Request"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
                      <p className="text-sm text-gray-900">{formatTimeAMPM(request.checkIn) || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
                      <p className="text-sm text-gray-900">{formatTimeAMPM(request.checkOut) || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                      <p className="text-sm text-gray-900 capitalize">{request.reason}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <p className="text-sm text-gray-900 break-words whitespace-normal first-letter:uppercase">{request.description}</p>
                    </div>
                    {(request.comments || request.rejectionReason) && (
                      <div className="md:col-span-2 mt-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {request.status === 'rejected' ? 'Rejection Reason' : 'Admin Comments'}
                        </label>
                        <p className="text-sm text-gray-900 break-words whitespace-pre-wrap first-letter:capitalize" >{request.comments || request.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center justify-between text-sm text-gray-500 gap-2">
                    <span>Submitted: {request.submittedAt ? formatDate(request.submittedAt, { includeTime: false }) : 'N/A'}</span>
                    {request.approvedDate && (
                      <span className="text-right">
                        {request.status === 'rejected' ? 'Rejected' : 'Approved'} by {request.approvedBy || 'Admin'} on {formatDate(request.approvedDate, { includeTime: false })}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pagination */}
        {renderPagination(
          regularizationPage,
          totalRegularizationPages,
          totalRegularizationRequests,
          regularizationPageSize,
          handleRegularizationPageChange,
          regularizationStartIndex,
          regularizationEndIndex
        )}
      </div>
    );
  };

  const renderCompOffOvertime = () => {
    // Calculate pagination for comp-off history
    const totalCompOffHistory = compOffData.history.length;
    const totalCompOffPages = Math.ceil(totalCompOffHistory / compOffPageSize);
    const compOffStartIndex = (compOffPage - 1) * compOffPageSize;
    const paginatedCompOffHistory = compOffData.history.slice(compOffStartIndex, compOffStartIndex + compOffPageSize);
    const compOffEndIndex = compOffStartIndex + paginatedCompOffHistory.length;

    const handleCompOffPageChange = (page) => {
      setCompOffPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Calculate pagination for overtime history
    const totalOvertimeHistory = overtimeData.history.length;
    const totalOvertimePages = Math.ceil(totalOvertimeHistory / overtimePageSize);
    const overtimeStartIndex = (overtimePage - 1) * overtimePageSize;
    const paginatedOvertimeHistory = overtimeData.history.slice(overtimeStartIndex, overtimeStartIndex + overtimePageSize);
    const overtimeEndIndex = overtimeStartIndex + paginatedOvertimeHistory.length;

    const handleOvertimePageChange = (page) => {
      setOvertimePage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-4">
        {/* Comp-Off */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Comp-Off</h3>
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{compOffData.available}</p>
                <p className="text-sm text-gray-500">Available</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{compOffData.used}</p>
                <p className="text-sm text-gray-500">Used</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{compOffData.pending}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {paginatedCompOffHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No comp-off history found</p>
              </div>
            ) : (
              paginatedCompOffHistory.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{formatDate(item.date, { includeTime: false })}</p>
                    <p className="text-sm text-gray-600 first-letter:uppercase">{item.reason}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{item.hours}h</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {getStatusIcon(item.status)}
                      <span className="ml-1 capitalize">{item.status}</span>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Comp-Off Pagination */}
        {renderPagination(
          compOffPage,
          totalCompOffPages,
          totalCompOffHistory,
          compOffPageSize,
          handleCompOffPageChange,
          compOffStartIndex,
          compOffEndIndex
        )}

        {/* Overtime */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Overtime</h3>
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{overtimeData.total}h</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{overtimeData.used}h</p>
                <p className="text-sm text-gray-500">Used</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">{overtimeData.pending}h</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {paginatedOvertimeHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>No overtime history found</p>
              </div>
            ) : (
              paginatedOvertimeHistory.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{formatDate(item.date, { includeTime: false })}</p>
                    <p className="text-sm text-gray-600 first-letter:uppercase">{item.reason}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{item.hours}h</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {getStatusIcon(item.status)}
                      <span className="ml-1 capitalize">{item.status}</span>
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Overtime Pagination */}
          {renderPagination(
            overtimePage,
            totalOvertimePages,
            totalOvertimeHistory,
            overtimePageSize,
            handleOvertimePageChange,
            overtimeStartIndex,
            overtimeEndIndex
          )}
        </div>
      </div>
    );
  };

  const renderHolidayCalendar = () => (
    <HolidayCalendar
      onBack={() => setActiveTab('attendance')}
      userRole="employee"
    />
  );

  const renderShiftRoster = () => {
    const totalRosterItems = rosterPagination.totalItems || shiftData.roster.length;
    const totalRosterPages =
      rosterPagination.totalPages ||
      Math.max(1, Math.ceil((rosterPagination.totalItems || shiftData.roster.length) / rosterPageSize));
    const rosterStartIndex = (rosterPage - 1) * rosterPageSize;
    const rosterEndIndex = rosterStartIndex + shiftData.roster.length;

    const handleRosterPageChange = (page) => {
      setRosterPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
      <div className="space-y-4">
        {/* Current Shift */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Current</h3>
            {shiftData.current.isRosterOverride && (
              <span className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                Roster Override
              </span>
            )}
          </div>

          {shiftData.current.isRosterOverride && shiftData.current.rosterDateRange && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This shift is from a roster assignment ({shiftData.current.rosterDateRange}).
                Your base shift will resume after this period.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Shift Details</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{shiftData.current.name || 'No shift assigned'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Start Time:</span>
                  <span className="font-medium">{formatTimeAMPM(shiftData.current.startTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">End Time:</span>
                  <span className="font-medium">{formatTimeAMPM(shiftData.current.endTime)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Break Duration:</span>
                  <span className="font-medium">{shiftData.current.breakDuration || 0} min</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">Working Days</h4>
              {shiftData.current.workingDays && shiftData.current.workingDays.length > 0 ? (
                <div className="space-y-1">
                  {shiftData.current.workingDays.map((day, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-gray-900 capitalize">{day}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No working days specified</p>
              )}
            </div>
          </div>
        </div>

        {/* Roster */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Roster</h3>
          </div>

          {totalRosterItems === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ClockIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No roster assignments found</p>
              <p className="text-sm mt-1">Your roster assignments will appear here when created by HR</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {shiftData.roster.map((roster, index) => (
                  <div key={`${roster.date || index}-${index}`} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <ClockIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {roster.date === roster.endDate
                            ? formatDate(roster.date, { includeTime: false })
                            : `${formatDate(roster.date, { includeTime: false })} - ${formatDate(roster.endDate, { includeTime: false })}`
                          }
                        </p>
                        <p className="text-sm text-gray-600 capitalize">{roster.shift}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Status: <span className={`font-medium capitalize ${roster.status === 'Active' ? 'text-green-600' :
                            roster.status === 'Upcoming' ? 'text-blue-600' :
                              'text-gray-600'
                            }`}>{roster.status}</span>
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {roster.startTime !== 'N/A' && roster.endTime !== 'N/A'
                          ? `${formatTimeAMPM(roster.startTime)} - ${formatTimeAMPM(roster.endTime)}`
                          : 'N/A'
                        }
                      </p>
                      {roster.startTime !== 'N/A' && roster.endTime !== 'N/A' && (
                        <p className="text-sm text-gray-600">
                          {(() => {
                            const start = roster.startTime.split(':');
                            const end = roster.endTime.split(':');
                            const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
                            let endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
                            if (endMinutes < startMinutes) endMinutes += 24 * 60;
                            const breakMins = roster.breakDuration || 0;
                            const totalHours = ((endMinutes - startMinutes - breakMins) / 60).toFixed(1);
                            return `${totalHours} hours`;
                          })()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Roster Pagination */}
          {renderPagination(
            rosterPage,
            totalRosterPages,
            totalRosterItems,
            rosterPageSize,
            handleRosterPageChange,
            rosterStartIndex,
            rosterEndIndex
          )}
        </div>
      </div>
    );
  };

  const renderRegularizationModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">New Regularization Request</h3>
          <button
            onClick={() => {
              setShowRegularizationModal(false);
              setRegularizationForm({ date: '', checkIn: '', checkOut: '', reason: '', reasonId: '', description: '' });
              setFormErrors({});
              setTouched({});
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleRegularizationSubmit(); }} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={regularizationForm.date}
                onChange={(e) => handleFieldChange('date', e.target.value)}
                onBlur={() => handleBlur('date')}
                onClick={(e) => e.target.showPicker()}
                required
                max={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer ${touched.date && formErrors.date ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              />
              {touched.date && formErrors.date && (
                <div className="mt-1 flex items-center text-red-600">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  <span className="text-xs">{formErrors.date}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                name="reason"
                value={regularizationForm.reasonId || regularizationForm.reason}
                onChange={(e) => handleFieldChange('reason', e.target.value)}
                onBlur={() => handleBlur('reason')}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${touched.reason && formErrors.reason ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              >
                <option value="">Select reason</option>
                {dynamicReasons.map((reason) => (
                  <option key={reason._id || reason.id} value={reason._id || reason.id}>
                    {reason.name}
                  </option>
                ))}
              </select>
              {touched.reason && formErrors.reason && (
                <div className="mt-1 flex items-center text-red-600">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  <span className="text-xs">{formErrors.reason}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Check In Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                name="checkIn"
                value={regularizationForm.checkIn}
                onChange={(e) => handleFieldChange('checkIn', e.target.value)}
                onBlur={() => handleBlur('checkIn')}
                onClick={(e) => e.target.showPicker()}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer ${touched.checkIn && formErrors.checkIn ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              />
              {touched.checkIn && formErrors.checkIn && (
                <div className="mt-1 flex items-center text-red-600">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  <span className="text-xs">{formErrors.checkIn}</span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Check Out Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                name="checkOut"
                value={regularizationForm.checkOut}
                onChange={(e) => handleFieldChange('checkOut', e.target.value)}
                onBlur={() => handleBlur('checkOut')}
                onClick={(e) => e.target.showPicker()}
                required
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer ${touched.checkOut && formErrors.checkOut ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
              />
              {touched.checkOut && formErrors.checkOut && (
                <div className="mt-1 flex items-center text-red-600">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  <span className="text-xs">{formErrors.checkOut}</span>
                </div>
              )}
            </div>
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span> <span className="text-gray-500 text-xs">(10-500 characters)</span>
            </label>
            <textarea
              name="description"
              value={regularizationForm.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              onBlur={() => handleBlur('description')}
              required
              rows={4}
              maxLength={500}
              placeholder="Please provide detailed explanation for the regularization request..."
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${touched.description && formErrors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
            />
            {touched.description && formErrors.description && (
              <div className="mt-1 flex items-center text-red-600">
                <AlertCircle className="h-3 w-3 mr-1" />
                <span className="text-xs">{formErrors.description}</span>
              </div>
            )}
            <div className="absolute bottom-2 right-2 flex justify-end">
              <span className={`text-xs ${regularizationForm.description.length >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                {regularizationForm.description.length}/500
              </span>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowRegularizationModal(false);
                setRegularizationForm({ date: '', checkIn: '', checkOut: '', reason: '', reasonId: '', description: '' });
                setFormErrors({});
                setTouched({});
              }}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <Send className="h-4 w-4" />
              <span>{loading ? 'Submitting...' : 'Submit Request'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const tabs = [
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'regularization', label: 'Regularization', icon: Edit },
    { id: 'holidays', label: 'Holidays', icon: CalendarIcon },
    { id: 'shift', label: 'Shift/Roster', icon: ClockIcon }
  ];

  // Render attendance details view (similar to HRMS)
  const renderAttendanceDetails = () => {
    if (!showAttendanceDetails || !selectedAttendanceRecord) {
      return null;
    }

    const record = selectedAttendanceRecord;
    const formattedDate = formatDate(record.date, { includeTime: false });

    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setShowAttendanceDetails(false);
                      setSelectedAttendanceRecord(null);
                    }}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Attendance Details
                  </h2>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Attendance Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Attendance Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Date:</span>
                      <span className="text-sm font-medium">
                        {formattedDate}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(record.status)}
                      <span className="text-sm text-gray-600">Status:</span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                          record.status
                        )}`}
                      >
                        {record.status
                          ? record.status.replace(/_/g, ' ')
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Check In:</span>
                      <span className="text-sm font-medium">
                        {formatTimeAMPM(record.checkIn)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Check Out:</span>
                      <span className="text-sm font-medium">
                        {formatTimeAMPM(record.checkOut)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Timer className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Working Hours:</span>
                      <span className="text-sm font-medium">
                        {calculateWorkingHours(record.checkIn, record.checkOut)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Summary
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Check In:</span>
                      <span className="text-sm font-medium">
                        {record.checkInPunches ? record.checkInPunches.length : (record.checkIn ? 1 : 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Check Out:</span>
                      <span className="text-sm font-medium">
                        {record.checkOutPunches ? record.checkOutPunches.length : (record.checkOut ? 1 : 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 pt-2 mt-2">
                      <span className="text-sm font-bold text-gray-700">Gross Working Hours:</span>
                      <span className="text-sm font-bold text-blue-600">
                        {calculateWorkingHours(record.checkIn, record.checkOut)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Punch History Section */}
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Punch History
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Check In Column */}
                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <div className="flex items-center gap-2 mb-3 border-b border-green-200 pb-2">
                      <div className="p-1 rounded-full bg-green-200">
                        <Clock className="h-4 w-4 text-green-700" />
                      </div>
                      <h4 className="font-semibold text-green-800">Check In</h4>
                    </div>
                    <div className="space-y-2">
                      {record.checkInPunches && record.checkInPunches.length > 0 ? (
                        [...record.checkInPunches].sort().map((time, idx) => (
                          <div key={`in-${idx}`} className="flex items-center justify-between bg-white px-3 py-2 rounded shadow-sm">
                            <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                            <span className="font-mono font-bold text-green-700">{formatTimeAMPM(time)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500 italic">No check-in records</div>
                      )}
                    </div>
                  </div>

                  {/* Check Out Column */}
                  <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-3 border-b border-red-200 pb-2">
                      <div className="p-1 rounded-full bg-red-200">
                        <Clock className="h-4 w-4 text-red-700" />
                      </div>
                      <h4 className="font-semibold text-red-800">Check Out</h4>
                    </div>
                    <div className="space-y-2">
                      {record.checkOutPunches && record.checkOutPunches.length > 0 ? (
                        [...record.checkOutPunches].sort().map((time, idx) => (
                          <div key={`out-${idx}`} className="flex items-center justify-between bg-white px-3 py-2 rounded shadow-sm">
                            <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                            <span className="font-mono font-bold text-red-700">{formatTimeAMPM(time)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500 italic">No check-out records</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Show details view if active
  if (showAttendanceDetails && selectedAttendanceRecord) {
    return renderAttendanceDetails();
  }

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Attendance</h1>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-blue-300'
                      }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        {activeTab === 'attendance' && renderAttendanceCalendar()}
        {activeTab === 'regularization' && renderRegularization()}
        {activeTab === 'holidays' && renderHolidayCalendar()}
        {activeTab === 'shift' && renderShiftRoster()}

        {showDayView && renderDayView()}
        {showRegularizationModal && renderRegularizationModal()}

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExportCurrentPage={handleExportCurrentPage}
          onExportRange={handleExportRange}
          onExportAll={handleExportAll}
          loading={exportLoading}
          type="Attendance"
        />
      </div>
    </div>
  );
};

export default MyAttendance;

